export type ResultFeed = 'auto' | 'manual';
export type DrawWindow = 'day' | 'night';
export type ConsensusStatus = 'pending' | 'confirmed' | 'disputed';

export const AUTO_PUBLISHER_ID = 'system:auto';

export interface ResultSubmission {
  id?: string;
  first: string;
  second: string;
  third: string;
  feed?: ResultFeed | string;
  publishedBy?: string;
  publishedByName?: string;
}

export interface ConsensusEvaluation {
  status: ConsensusStatus;
  triple?: [string, string, string];
  confirmations: number;
  requiredConfirmations: number;
  conflictingTriples?: Array<{
    triple: string;
    count: number;
    publishers: string[];
  }>;
}

export function normalizeTriple(
  first: string,
  second: string,
  third: string,
): [string, string, string] | null {
  const f = String(first ?? '').padStart(2, '0').slice(-2);
  const s = String(second ?? '').padStart(2, '0').slice(-2);
  const t = String(third ?? '').padStart(2, '0').slice(-2);
  if (!/^\d{2}$/.test(f) || !/^\d{2}$/.test(s) || !/^\d{2}$/.test(t)) return null;
  return [f, s, t];
}

export function tripleKey(input: Pick<ResultSubmission, 'first' | 'second' | 'third'>): string {
  const triple = normalizeTriple(input.first, input.second, input.third);
  return triple ? triple.join('-') : '';
}

export function buildDrawSessionKey(dayKey: string, window: DrawWindow): string {
  return `${dayKey}_${window}`;
}

export function resolveRequiredConfirmations(
  bankerCount: number,
  configured?: number | null,
): number {
  if (configured != null && configured >= 1) return Math.floor(configured);
  return bankerCount > 1 ? 2 : 1;
}

export function dedupeSubmissions(submissions: ResultSubmission[]): ResultSubmission[] {
  const latest = new Map<string, ResultSubmission>();
  for (const submission of submissions) {
    const publisher = submission.feed === 'auto'
      ? AUTO_PUBLISHER_ID
      : (submission.publishedBy ?? submission.id ?? 'unknown');
    latest.set(publisher, submission);
  }
  return [...latest.values()];
}

/** Decide whether a draw session is confirmed, pending, or disputed. */
export function evaluateResultConsensus(
  submissions: ResultSubmission[],
  requiredConfirmations: number,
): ConsensusEvaluation {
  const unique = dedupeSubmissions(submissions).filter((s) => tripleKey(s));

  if (unique.length === 0) {
    return { status: 'pending', confirmations: 0, requiredConfirmations };
  }

  const groups = new Map<string, {
    triple: [string, string, string];
    publishers: string[];
  }>();

  for (const submission of unique) {
    const key = tripleKey(submission);
    const triple = normalizeTriple(submission.first, submission.second, submission.third)!;
    const publisher = submission.feed === 'auto'
      ? AUTO_PUBLISHER_ID
      : (submission.publishedBy ?? submission.id ?? 'unknown');
    const group = groups.get(key) ?? { triple, publishers: [] };
    if (!group.publishers.includes(publisher)) group.publishers.push(publisher);
    groups.set(key, group);
  }

  const ranked = [...groups.values()].sort((a, b) => b.publishers.length - a.publishers.length);
  const top = ranked[0];

  if (unique.length === 1 && unique[0].feed === 'auto') {
    return {
      status: 'confirmed',
      triple: top.triple,
      confirmations: 1,
      requiredConfirmations: 1,
    };
  }

  if (top.publishers.length >= requiredConfirmations) {
    return {
      status: 'confirmed',
      triple: top.triple,
      confirmations: top.publishers.length,
      requiredConfirmations,
    };
  }

  if (ranked.length > 1) {
    return {
      status: 'disputed',
      confirmations: top.publishers.length,
      requiredConfirmations,
      conflictingTriples: ranked.map((group) => ({
        triple: group.triple.join('-'),
        count: group.publishers.length,
        publishers: group.publishers,
      })),
    };
  }

  return {
    status: 'pending',
    triple: top.triple,
    confirmations: top.publishers.length,
    requiredConfirmations,
  };
}
